"use client";

import { useQuery } from "@tanstack/react-query";
import { useToken } from "@trydig/centra/client";
import { getDPC } from "@trydig/events/depict";
import { env } from "@/env.mjs";
import { useGeo } from "@/i18n/routing";

/**
 * Lazily initialize a singleton DPC instance for the active market + Centra
 * session. `auto_observe: true` makes DPC watch the DOM for product cards with
 * the data attributes returned by `getProductCardProps` and fire impression +
 * click events automatically.
 *
 * The `getProductCardProps` helper itself lives in `./tracking.ts` (no
 * `"use client"` directive) so server components can render the data
 * attributes during SSR without crossing this client boundary.
 */
export const useDPC = () => {
	const { country } = useGeo();
	const token = useToken();
	return useQuery({
		queryKey: ["depict-dpc", token, country],
		queryFn: async () => {
			const dpc = await getDPC(env.NEXT_PUBLIC_DEPICT_MERCHANT, {
				auto_observe: true,
				market: country.toLowerCase(),
			});
			if (token) dpc.setSessionId(token);
			return dpc;
		},
		enabled: Boolean(token),
		// DPC keeps internal state (session, product context). Don't refetch.
		staleTime: Number.POSITIVE_INFINITY,
	});
};
